import { EDIT_PAGE } from "context/editor/editorActions";

/**
 * Permet de recuperer dans le store le nombre de brouillon existant.
 *
 * @param {any} state - L'état du store.
 * @returns {number} Le nombre de brouillon.
 */
export const getDraftNumber = (state) => {
  return state.documents.filter(doc => doc?.id?.includes('brouillon')).length;
};

/**
 * Permet de recuperer dans le store le nombre de brouillon existant.
 *
 * @param {any} state - L'état du store.
 * @returns {number} Le nombre de brouillon.
 */
export const getDocumentNumber = (state) => {
  return state.documents.length;
};

export function getUsedSmartfields(document) {
  const smartfieldSet = new Set();

  // Expressions régulières pour détecter les patterns
  const financialRegex = /^(prestation|opco|entreprise)(\d+|Total)$/;
  const spendingRegex = /^(hebergement|restauration)(Nbr|Montant)(\d+|Total)$/;

  document?.pages?.forEach(page => {
    const matches = page?.text?.matchAll(/data-smartfield="\[([\w\/]+?)\/\]"/g);

    for (const match of matches) {
      const fieldName = match[1];

      // Vérification des catégories
      if (financialRegex.test(fieldName)) {
        smartfieldSet.add('[tableauFinancier/]');
      } else if (spendingRegex.test(fieldName)) {
        smartfieldSet.add('[totalDepenses/]');
      } else {
        smartfieldSet.add(`[${fieldName}/]`);
      }
    }
  });

  return Array.from(smartfieldSet);
}

export const getUsedTextColors = (selectedDocument) => {
  const allColors = new Set();

  if (!selectedDocument || !selectedDocument.pages) return [];

  selectedDocument.pages.forEach(page => {
    const html = page.text || '';

    // 1. Couleurs dans style="color: #xxxxxx"
    for (const match of html.matchAll(/style\s*=\s*"(?:[^"]*?)color\s*:\s*(#(?:[A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}))/g)) {
      allColors.add(normalizeHex(match[1]));
    }

    // 2. Couleurs dans balise <font color="#xxxxxx">
    for (const match of html.matchAll(/<font[^>]*color=["'](#(?:[A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}))["']/gi)) {
      allColors.add(normalizeHex(match[1]));
    }
  });

  return Array.from(allColors);
};

// Helper pour normaliser les hex courts (#abc → #aabbcc)
function normalizeHex(hex) {
  hex = hex.toLowerCase();
  if (hex.length === 4) {
    return (
      '#' +
      hex[1] + hex[1] +
      hex[2] + hex[2] +
      hex[3] + hex[3]
    );
  }
  return hex;
}



export function selectedDocumentFromStore(selectedDocument, state) {
  const thisDocumentFormStore = state.documents.find(d => d.id === selectedDocument.id);

  return thisDocumentFormStore
}

export function saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch) {
  dispatch({
    type: EDIT_PAGE,
    payload: {
      documentId: selectedDocument?.id,
      pageId: `page${currentPageIndex}`,
      text: editorRef.current?.innerHTML,
    }
  });
}

export const insertSmartField = ({
  field,
  editorRef,
  setSelectedConvention,
  selectedDocument,
  editorStore: { dispatch, state },
  currentPageIndex
}) => {
  if (!editorRef?.current) return;


  const editor = editorRef.current;
  editor.focus();

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return;

  // Remove any selected content.
  range.deleteContents();

  // Create and configure the smart field span.
  const span = document.createElement("span");
  span.textContent = ` ${field.label} `;
  span.setAttribute("data-smartfield", field.value);
  span.setAttribute("contenteditable", "false");
  span.style.userSelect = "none";
  span.style.backgroundColor = "#f3f3f3";
  span.style.border = "1px solid #ccc";
  span.style.padding = "2px 4px";
  span.style.borderRadius = "4px";

  // Insert the smart field span.
  range.insertNode(span);

  // Insert a trailing text node (a space) for a caret landing spot.
  const trailingNode = document.createTextNode(" ");
  span.parentNode.insertBefore(trailingNode, span.nextSibling);

  // Move the caret immediately after the trailing text node.
  range.setStartAfter(trailingNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);

  // Defer the state update slightly to let the DOM settle.
  setTimeout(() => {
    setSelectedConvention(prev => ({
      ...prev,
      content: editor.innerHTML,
    }));
  }, 50);

  dispatch({
    type: EDIT_PAGE,
    payload: {
      documentId: selectedDocument?.id,
      pageId: `page${currentPageIndex}`,
      text: editorRef.current?.innerHTML,
    }
  });

};

export const insertTable = ({
  TableComponent,
  editorRef,
  setSelectedConvention,
  selectedDocument,
  editorStore: { dispatch, state },
  currentPageIndex,
  ReactDOMServer
}) => {
  if (!editorRef?.current) return;
  const editor = editorRef?.current;
  editor.focus();

  // Convert the passed table component to an HTML string.
  const tableHTML = ReactDOMServer.renderToStaticMarkup(<TableComponent />);

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  range.collapse(false);

  // Create a temporary container and insert the HTML.
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = tableHTML;
  const tableNode = tempDiv.firstChild;

  range.insertNode(tableNode);

  // Move the caret immediately after the inserted table.
  range.setStartAfter(tableNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);

  // Update the state with the new HTML.
  setSelectedConvention(prev => ({
    ...prev,
    content: editor.innerHTML,
  }));

  dispatch({
    type: EDIT_PAGE,
    payload: {
      documentId: selectedDocument?.id,
      pageId: `page${currentPageIndex}`,
      text: editorRef.current?.innerHTML,
    }
  });
};

/**
 * Récupere les nodes entre startNode et endNode.
 *
 * @param {Node} startNode - Node de depart.
 * @param {Node} endNode - Node de fin.
 * @returns {Node[]} Tableau des nodes entre startNode et endNode.
 */
export const getNodesBetween = (startNode, endNode) => {
  const nodes = [];
  let currentNode = startNode;

  while (currentNode && currentNode !== endNode) {
    nodes.push(currentNode);
    currentNode = currentNode.nextSibling;
  }

  if (endNode) {
    nodes.push(endNode);
  }

  return nodes;
}

export const handleFindDocumentByIdInStore = (documentId, state) => {

  let document = state.documents.find((doc) => doc.id === documentId);

  return document;
};
